import React, { useEffect, useState } from 'react';
import {
  Box,
  Collapse,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Stack,
  useTheme,
  useMediaQuery,
  Button
} from '@mui/material';
import {
  AccountBalanceWallet,
  Assessment,
  Business,
  Dashboard as DashboardIcon,
  ExpandLess,
  ExpandMore,
  FactCheck,
  Key,
  Menu as MenuIcon,
  MenuBook,
  Payment,
  Person,
  Refresh as RefreshIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../services/authService';
import { Permission, UserRole } from '../types';

const drawerWidth = 264;
const openedTabsStorageKey = 'cashgit-opened-page-tabs';
const maxOpenedTabs = 10;

interface LayoutProps {
  children?: React.ReactNode;
  renderPage?: (path: string) => React.ReactNode;
}

interface MenuItemConfig {
  text: string;
  path: string;
  permissions?: Permission[];
  anyPermission?: Permission[];
  requiredRole?: UserRole;
}

interface MenuGroupConfig {
  text: string;
  icon: React.ReactNode;
  items: MenuItemConfig[];
}

interface OpenedTab {
  text: string;
  path: string;
}

const dashboardItem: MenuItemConfig = {
  text: '仪表板',
  path: '/',
  permissions: []
};

const menuGroups: MenuGroupConfig[] = [
  {
    text: '订单管理',
    icon: <FactCheck fontSize="small" />,
    items: [
      {
        text: '代收订单',
        path: '/collection-orders',
        anyPermission: [Permission.VIEW_ALL_TRANSACTIONS, Permission.VIEW_OWN_TRANSACTIONS]
      },
      {
        text: '代付订单',
        path: '/payout-orders',
        anyPermission: [Permission.VIEW_ALL_TRANSACTIONS, Permission.VIEW_OWN_TRANSACTIONS]
      },
      {
        text: 'UTR补单',
        path: '/utr-applications',
        anyPermission: [Permission.MANAGE_USERS, Permission.SYSTEM_MONITORING]
      }
    ]
  },
  {
    text: '商户管理',
    icon: <Business fontSize="small" />,
    items: [
      {
        text: '商户列表',
        path: '/merchants',
        anyPermission: [Permission.VIEW_ALL_MERCHANTS, Permission.VIEW_AGENT_MERCHANTS]
      },
      {
        text: '客户管理',
        path: '/customer-management',
        anyPermission: [Permission.MANAGE_USERS, Permission.SYSTEM_MONITORING]
      },
      {
        text: '代理管理',
        path: '/agent-management',
        permissions: [Permission.MANAGE_USERS]
      },
      {
        text: '商户统计',
        path: '/merchant-statistics',
        anyPermission: [Permission.VIEW_AGENT_STATISTICS, Permission.MANAGE_USERS, Permission.SYSTEM_MONITORING]
      }
    ]
  },
  {
    text: '支付配置',
    icon: <Payment fontSize="small" />,
    items: [
      {
        text: '支付账户',
        path: '/payment-accounts',
        permissions: [Permission.VIEW_PAYMENT_CONFIG]
      },
      {
        text: '通道采集',
        path: '/channel-collection',
        anyPermission: [Permission.MANAGE_USERS, Permission.SYSTEM_MONITORING]
      },
      {
        text: '支付URL通道',
        path: '/payment-url-channels',
        requiredRole: UserRole.ADMIN
      },
      {
        text: '支付测试',
        path: '/payment-test',
        requiredRole: UserRole.ADMIN
      }
    ]
  },
  {
    text: '资金结算',
    icon: <AccountBalanceWallet fontSize="small" />,
    items: [
      {
        text: '结算管理',
        path: '/settlement-management',
        requiredRole: UserRole.ADMIN
      },
      {
        text: '余额调整',
        path: '/balance-adjustment',
        anyPermission: [Permission.MANAGE_USERS, Permission.SYSTEM_MONITORING]
      },
      {
        text: '投诉扣款',
        path: '/complaint-deductions',
        anyPermission: [Permission.MANAGE_USERS, Permission.SYSTEM_MONITORING]
      },
      {
        text: '承兑审核',
        path: '/acceptance-management',
        requiredRole: UserRole.ADMIN
      }
    ]
  },
  {
    text: '系统管理',
    icon: <Assessment fontSize="small" />,
    items: [
      {
        text: '用户管理',
        path: '/users',
        permissions: [Permission.MANAGE_USERS]
      }
    ]
  },
  {
    text: '商户中心',
    icon: <Key fontSize="small" />,
    items: [
      {
        text: '密钥管理',
        path: '/merchant-keys',
        requiredRole: UserRole.MERCHANT
      },
      {
        text: '承兑申请',
        path: '/acceptance-request',
        requiredRole: UserRole.MERCHANT
      },
      {
        text: '投诉记录',
        path: '/complaint-records',
        requiredRole: UserRole.MERCHANT
      },
      {
        text: 'UTR补单申请',
        path: '/utr-application',
        requiredRole: UserRole.MERCHANT
      },
      {
        text: 'API文档',
        path: '/api-docs',
        requiredRole: UserRole.MERCHANT
      }
    ]
  }
];

const profileItem: MenuItemConfig = {
  text: '个人资料',
  path: '/profile',
  requiredRole: UserRole.MERCHANT
};

const allKnownMenuItems: MenuItemConfig[] = [
  dashboardItem,
  profileItem,
  ...menuGroups.flatMap(group => group.items)
];

const getStoredTabs = () => {
  try {
    const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (navigationEntry?.type === 'reload') {
      localStorage.removeItem(openedTabsStorageKey);
      return [];
    }

    const raw = localStorage.getItem(openedTabsStorageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((tab) => tab && typeof tab.text === 'string' && typeof tab.path === 'string')
      .slice(0, maxOpenedTabs);
  } catch (error) {
    return [];
  }
};

const canUserShowMenuItem = (user: ReturnType<typeof authService.getCurrentUser>, item: MenuItemConfig) => {
  if (!user) return false;
  if (item.requiredRole && user.role !== item.requiredRole) return false;
  if (item.permissions && item.permissions.length > 0) {
    return item.permissions.some(permission => authService.hasPermission(permission));
  }
  if (item.anyPermission && item.anyPermission.length > 0) {
    return authService.hasAnyPermission(item.anyPermission);
  }
  return true;
};

export default function Layout({ children, renderPage }: LayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [openedTabs, setOpenedTabs] = useState<OpenedTab[]>(getStoredTabs);
  const [refreshKeys, setRefreshKeys] = useState<Record<string, number>>({});
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const currentUser = authService.getCurrentUser();

  const canShowMenuItem = (item: MenuItemConfig) => {
    return canUserShowMenuItem(currentUser, item);
  };

  const visibleGroups = menuGroups
    .map(group => ({
      ...group,
      items: group.items.filter(canShowMenuItem)
    }))
    .filter(group => group.items.length > 0);

  const isGroupActive = (group: MenuGroupConfig) => {
    return group.items.some(item => location.pathname === item.path);
  };

  const isGroupOpen = (group: MenuGroupConfig) => {
    return openGroups[group.text] ?? isGroupActive(group);
  };

  useEffect(() => {
    const currentItem = allKnownMenuItems.find(item => item.path === location.pathname);
    if (!currentItem || !canUserShowMenuItem(currentUser, currentItem)) return;

    setOpenedTabs(prevTabs => {
      const existingIndex = prevTabs.findIndex(tab => tab.path === currentItem.path);
      const nextTabs = existingIndex >= 0
        ? prevTabs.map((tab, index) => (
            index === existingIndex ? { text: currentItem.text, path: currentItem.path } : tab
          ))
        : [...prevTabs, { text: currentItem.text, path: currentItem.path }].slice(-maxOpenedTabs);
      localStorage.setItem(openedTabsStorageKey, JSON.stringify(nextTabs));
      return nextTabs;
    });
  }, [location.pathname, currentUser]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
    handleProfileMenuClose();
  };

  const handleProfile = () => {
    handleNavigate('/profile');
    handleProfileMenuClose();
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const toggleGroup = (groupText: string) => {
    const targetGroup = visibleGroups.find(group => group.text === groupText);
    const defaultOpen = targetGroup ? isGroupActive(targetGroup) : false;
    setOpenGroups(prev => ({
      ...prev,
      [groupText]: !(prev[groupText] ?? defaultOpen)
    }));
  };

  const handleRefreshCurrentTab = () => {
    setRefreshKeys(prev => ({
      ...prev,
      [location.pathname]: Date.now()
    }));
  };

  const handleCloseTab = (path: string) => {
    setOpenedTabs(prevTabs => {
      const currentIndex = prevTabs.findIndex(tab => tab.path === path);
      const nextTabs = prevTabs.filter(tab => tab.path !== path);
      localStorage.setItem(openedTabsStorageKey, JSON.stringify(nextTabs));

      if (path === location.pathname) {
        const nextActiveTab = nextTabs[currentIndex] || nextTabs[currentIndex - 1];
        navigate(nextActiveTab?.path || '/');
      }

      return nextTabs;
    });

    setRefreshKeys(prev => {
      const next = { ...prev };
      delete next[path];
      return next;
    });
  };

  const visibleOpenedTabs = openedTabs.filter(tab => {
    const item = allKnownMenuItems.find(menuItem => menuItem.path === tab.path);
    return item ? canShowMenuItem(item) : false;
  });

  const currentRefreshKey = refreshKeys[location.pathname] || 0;
  const currentPageItem = allKnownMenuItems.find(item => item.path === location.pathname);
  const tabsForContent = renderPage && currentPageItem && canShowMenuItem(currentPageItem) && !visibleOpenedTabs.some(tab => tab.path === currentPageItem.path)
    ? [...visibleOpenedTabs, { text: currentPageItem.text, path: currentPageItem.path }]
    : visibleOpenedTabs;

  const drawer = (
    <Box sx={{ height: '100%', bgcolor: '#ffffff', color: '#1f2937', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ minHeight: '72px !important', px: 3 }}>
        <Box>
          <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 800, letterSpacing: 0.2, color: '#111827' }}>
            CashGit
          </Typography>
          <Typography variant="caption" sx={{ color: '#6b7280', letterSpacing: 0.8, textTransform: 'uppercase' }}>
            Payment Console
          </Typography>
        </Box>
      </Toolbar>
      <List sx={{ px: 1.5, py: 1, flex: 1, overflowY: 'auto' }}>
        {canShowMenuItem(dashboardItem) && (
          <ListItem disablePadding sx={{ mb: 0.75 }}>
            <ListItemButton
              selected={location.pathname === dashboardItem.path}
              onClick={() => handleNavigate(dashboardItem.path)}
              sx={{
                minHeight: 44,
                borderRadius: 2,
                color: location.pathname === dashboardItem.path ? '#111827' : '#374151',
                '&.Mui-selected': {
                  bgcolor: 'rgba(15, 23, 42, 0.08)',
                  boxShadow: 'inset 3px 0 0 #6b7280'
                },
                '&.Mui-selected:hover': {
                  bgcolor: 'rgba(15, 23, 42, 0.11)'
                },
                '&:hover': {
                  bgcolor: 'rgba(15, 23, 42, 0.06)'
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 38, color: 'inherit' }}>
                <DashboardIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={dashboardItem.text}
                sx={{ '& .MuiListItemText-primary': { fontSize: '0.92rem', fontWeight: 700 } }}
              />
            </ListItemButton>
          </ListItem>
        )}
        <Divider sx={{ my: 1.25, borderColor: 'rgba(15, 23, 42, 0.08)' }} />
        {visibleGroups.map((group) => {
          const active = isGroupActive(group);
          const open = isGroupOpen(group);

          return (
            <Box key={group.text} sx={{ mb: 0.5 }}>
              <ListItem disablePadding>
                <ListItemButton
                  selected={active}
                  onClick={() => toggleGroup(group.text)}
                  sx={{
                    minHeight: 42,
                    borderRadius: 2,
                    color: active ? '#111827' : '#374151',
                    '&.Mui-selected': {
                      bgcolor: 'rgba(15, 23, 42, 0.08)'
                    },
                    '&.Mui-selected:hover': {
                      bgcolor: 'rgba(15, 23, 42, 0.11)'
                    },
                    '&:hover': {
                      bgcolor: 'rgba(15, 23, 42, 0.06)'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 38, color: active ? '#111827' : '#6b7280' }}>
                    {group.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={group.text}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontSize: '0.9rem',
                        fontWeight: active ? 800 : 650
                      }
                    }}
                  />
                  {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </ListItemButton>
              </ListItem>
              <Collapse in={open} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ mt: 0.5, mb: 0.75, pl: 1.5 }}>
                  {group.items.map((item) => {
                    const selected = location.pathname === item.path;
                    return (
                      <ListItem key={item.text} disablePadding sx={{ mb: 0.25 }}>
                        <ListItemButton
                          selected={selected}
                          onClick={() => handleNavigate(item.path)}
                          sx={{
                            minHeight: 36,
                            pl: 4,
                            py: 0.75,
                            borderRadius: 1.5,
                            color: selected ? '#111827' : '#6b7280',
                            position: 'relative',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              left: 16,
                              width: 5,
                              height: 5,
                              borderRadius: '50%',
                              bgcolor: selected ? '#6b7280' : 'rgba(107, 114, 128, 0.35)'
                            },
                            '&.Mui-selected': {
                              bgcolor: 'rgba(15, 23, 42, 0.07)'
                            },
                            '&.Mui-selected:hover': {
                              bgcolor: 'rgba(15, 23, 42, 0.1)'
                            },
                            '&:hover': {
                              bgcolor: 'rgba(15, 23, 42, 0.06)',
                              color: '#111827'
                            }
                          }}
                        >
                          <ListItemText
                            primary={item.text}
                            sx={{
                              '& .MuiListItemText-primary': {
                                fontSize: '0.84rem',
                                fontWeight: selected ? 700 : 500
                              }
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </List>
      <Box
        sx={{
          p: 1.5,
          borderTop: '1px solid rgba(15, 23, 42, 0.08)',
          bgcolor: '#fff'
        }}
      >
        {currentUser?.role === 'merchant' && (
          <Button
            fullWidth
            startIcon={<MenuBook />}
            onClick={() => navigate('/api-docs')}
            sx={{
              mb: 1,
              justifyContent: 'flex-start',
              textTransform: 'none',
              borderRadius: 2,
              color: '#2563eb',
              bgcolor: 'rgba(37, 99, 235, 0.08)',
              '&:hover': {
                bgcolor: 'rgba(37, 99, 235, 0.14)'
              }
            }}
          >
            API文档
          </Button>
        )}
        <Button
          fullWidth
          onClick={handleProfileMenuOpen}
          sx={{
            justifyContent: 'flex-start',
            gap: 1.25,
            p: 1,
            borderRadius: 2,
            textTransform: 'none',
            color: '#111827',
            '&:hover': {
              bgcolor: 'rgba(15, 23, 42, 0.06)'
            }
          }}
        >
          <Avatar sx={{ width: 36, height: 36, bgcolor: '#111827' }}>
            <Person />
          </Avatar>
          <Box sx={{ minWidth: 0, textAlign: 'left' }}>
            <Typography noWrap sx={{ fontSize: 14, fontWeight: 700 }}>
              {currentUser?.fullName || currentUser?.username}
            </Typography>
            <Typography noWrap sx={{ fontSize: 12, color: '#6b7280' }}>
              {currentUser?.role === 'admin' ? '系统管理员' : currentUser?.role}
            </Typography>
          </Box>
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: 0,
              bgcolor: '#ffffff'
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: 0,
              bgcolor: '#ffffff',
              boxShadow: '10px 0 28px rgba(15, 23, 42, 0.06)'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          bgcolor: '#fff',
          backgroundImage: 'none',
        }}
      >
        <Box
          sx={{
            p: 0,
          }}
        >
          <IconButton
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            sx={{
              mb: 1.5,
              display: { xs: 'inline-flex', md: 'none' },
              color: '#0f172a',
              bgcolor: '#fff',
              boxShadow: '0 8px 20px rgba(15, 23, 42, 0.08)',
              '&:hover': {
                bgcolor: '#f8fafc'
              }
            }}
          >
            <MenuIcon />
          </IconButton>
          {visibleOpenedTabs.length > 0 && (
            <Box
              sx={{
                mb: 0,
                px: 0,
                bgcolor: 'transparent',
              }}
            >
              <Stack
                direction="row"
                spacing={0.5}
                sx={{
                  overflowX: 'auto',
                  alignItems: 'flex-end',
                  '&::-webkit-scrollbar': {
                    height: 6
                  },
                  '&::-webkit-scrollbar-thumb': {
                    bgcolor: 'rgba(100, 116, 139, 0.25)',
                    borderRadius: 999
                  }
                }}
              >
                {visibleOpenedTabs.map((tab) => {
                  const selected = tab.path === location.pathname;
                  return (
                    <Box
                      role="button"
                      tabIndex={0}
                      key={tab.path}
                      onClick={() => handleNavigate(tab.path)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleNavigate(tab.path);
                        }
                      }}
                      sx={{
                        flex: '0 0 auto',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: selected ? 0.65 : 0,
                        maxWidth: selected ? 228 : 156,
                        minWidth: 82,
                        height: 42,
                        px: selected ? 0.9 : 1.75,
                        borderTopLeftRadius: '2px',
                        borderTopRightRadius: '2px',
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                        border: '1px solid',
                        borderColor: 'rgba(15, 23, 42, 0.08)',
                        borderBottomColor: selected ? '#fff' : 'rgba(15, 23, 42, 0.08)',
                        mb: '-1px',
                        position: 'relative',
                        zIndex: selected ? 1 : 0,
                        bgcolor: selected ? '#fff' : 'rgba(15, 23, 42, 0.07)',
                        color: selected ? '#1677ff' : '#64748b',
                        cursor: 'pointer',
                        fontSize: 14,
                        fontWeight: selected ? 800 : 600,
                        whiteSpace: 'nowrap',
                        boxShadow: 'none',
                        transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease',
                        '&:hover': {
                          bgcolor: selected ? '#fff' : 'rgba(15, 23, 42, 0.1)',
                          color: selected ? '#1677ff' : '#0f172a'
                        }
                      }}
                    >
                      {selected && (
                        <IconButton
                          size="small"
                          aria-label="刷新当前页面"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleRefreshCurrentTab();
                          }}
                          sx={{
                            width: 24,
                            height: 24,
                            color: '#1677ff',
                            flexShrink: 0,
                            '&:hover': {
                              bgcolor: 'rgba(0, 0, 0, 0.06)'
                            }
                          }}
                        >
                          <RefreshIcon sx={{ fontSize: 19 }} />
                        </IconButton>
                      )}
                      <Box
                        component="span"
                        title={tab.text}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          textAlign: 'center'
                        }}
                      >
                        {tab.text}
                      </Box>
                      {selected && (
                        <IconButton
                          size="small"
                          aria-label="关闭标签页"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleCloseTab(tab.path);
                          }}
                          sx={{
                            width: 24,
                            height: 24,
                            color: '#1677ff',
                            flexShrink: 0,
                            '&:hover': {
                              color: '#1677ff',
                              bgcolor: 'rgba(0, 0, 0, 0.06)'
                            }
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 19 }} />
                        </IconButton>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}
          <Box
            sx={{
              minHeight: 'calc(100vh - 154px)',
              p: { xs: 2, md: 3 },
              bgcolor: '#fff',
              border: 0,
              borderRadius: 0,
              boxShadow: 'none',
            }}
          >
            {renderPage ? (
              tabsForContent.map((tab) => {
                const selected = tab.path === location.pathname;
                const refreshKey = refreshKeys[tab.path] || 0;
                return (
                  <Box
                    key={`${tab.path}:${refreshKey}`}
                    sx={{ display: selected ? 'block' : 'none' }}
                  >
                    {renderPage(tab.path)}
                  </Box>
                );
              })
            ) : (
              <Box key={`${location.pathname}:${currentRefreshKey}`}>
                {children}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
      >
        <MenuItem onClick={handleProfile}>个人资料</MenuItem>
        <MenuItem onClick={handleLogout}>退出登录</MenuItem>
      </Menu>
    </Box>
  );
}
